import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import { useEntity, useUpdateEntity } from '@/models/entity';
import { LocaleInputs } from '@/routes/Settings/shared/LocaleInputs';
import { EditButton, SaveButton } from '@/routes/SettingsBeta/components/SaveEditCancelButtons';
import { I18nWording } from '@/services/i18n';
import { trimI18nWording } from '@/utils/trimI18nWording';

export const GeneralTermsAndConditions = ({ entityId }: { entityId: string }) => {
  const { t } = useLingui();
  const { data: entity } = useEntity(entityId);
  const { mutateAsync: updateEntity, isPending: isUpdateEntityPending } = useUpdateEntity(
    entity?.id
  );

  const [generalTermsAndConditions, setGeneralTermsAndConditions] = useState<I18nWording>(
    entity?.generalTermsAndConditions ?? { en: '', fr: '' }
  );
  const [isEditing, setIsEditing] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  if (!entity) {
    return null;
  }

  const handleSave = async () => {
    const hasError =
      (!!generalTermsAndConditions.en && !generalTermsAndConditions.fr) ||
      (!generalTermsAndConditions.en && !!generalTermsAndConditions.fr);

    if (hasError) {
      setShowErrors(true);

      return;
    }
    setShowErrors(false);

    await updateEntity({
      generalTermsAndConditions: trimI18nWording(generalTermsAndConditions),
    });
    setIsEditing(false);
  };

  return (
    <Stack gap="1rem">
      <Title as="h3" size="300">
        <Stack row gap="1rem" justifyContent="space-between" alignItems="center">
          <Trans id="settings.entities.general-terms-and-conditions.title">
            General terms and conditions
          </Trans>
          {isEditing ? (
            <SaveButton
              label={t({
                id: 'settings.entities.general-terms-and-conditions.save.label',
                message: 'Save',
              })}
              onClick={handleSave}
              isLoading={isUpdateEntityPending}
            />
          ) : (
            <EditButton
              label={t({
                id: 'settings.entities.general-terms-and-conditions.edit.label',
                message: 'Edit general terms and conditions',
              })}
              onClick={() => setIsEditing(true)}
            />
          )}
        </Stack>
      </Title>

      <Box>
        <Stack gap="0.5rem">
          <LocaleInputs
            label={t({
              id: 'settings.entities.general-terms-and-conditions.label.label',
              message: 'Label',
            })}
            placeholder={t({
              id: 'settings.entities.general-terms-and-conditions.label.label',
              message: 'Label',
            })}
            type="text-area"
            value={generalTermsAndConditions}
            setValue={(newValue) => {
              setGeneralTermsAndConditions(newValue);
            }}
            disabled={!isEditing || isUpdateEntityPending}
            showErrors={showErrors}
          />

          {showErrors && (
            <Message type="error">
              <Trans id="settings.entities.general-terms-and-conditions.errors.missing-translations">
                Please fill in translations for general terms and conditions
              </Trans>
            </Message>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};
